import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React from 'react'
import { useMutation, useQuery } from '../../../api/graphql'
import { extractDbIdFromGraphqlId } from '../../../util/string'
import { Hugeicon } from '../../../icons/Hugeicon'

const SOCIAL_ACCOUNTS_QUERY = gql`
  query SocialAccounts {
    currentUser {
      id
      organizationMemberships {
        role
        organization {
          id
          socialAccounts {
            id
            platform
            platformAccountId
            platformAccountName
            platformAccountImageUrl
            status
          }
        }
      }
    }
  }
`

const DISCONNECT_MUTATION = gql`
  mutation SocialAccountDisconnect($id: ID!) {
    socialAccountDisconnect(id: $id) {
      socialAccount {
        id
        status
      }
      errors {
        attribute
        message
      }
    }
  }
`

type SocialAccount = {
  id: string
  platform: string
  platformAccountId: string
  platformAccountName: string | null
  platformAccountImageUrl: string | null
  status: string
}

type Props = {
  organizationId: string
}

const SocialAccountsManager: React.FC<Props> = ({ organizationId }) => {
  const { data, loading, refetch } = useQuery(SOCIAL_ACCOUNTS_QUERY, {
    variables: { organizationId },
  })
  const [disconnectAccount] = useMutation(DISCONNECT_MUTATION)

  const accounts: SocialAccount[] =
    data?.currentUser?.organizationMemberships
      ?.find((m: { organization: { id: string } }) => m.organization.id === organizationId)
      ?.organization?.socialAccounts || []

  const handleConnect = () => {
    const dbId = extractDbIdFromGraphqlId(organizationId)
    window.location.href = `/oauth/meta/authorize?organization_id=${dbId}`
  }

  const handleDisconnect = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) return

    await disconnectAccount({ variables: { id: accountId } })
    refetch()
  }

  const platformLabel = (platform: string) => {
    switch (platform) {
      case 'facebook_page':
        return 'Facebook Page'
      case 'instagram_business':
        return 'Instagram'
      default:
        return platform
    }
  }

  if (loading) {
    return <LoadingText>Loading social accounts...</LoadingText>
  }

  return (
    <Container>
      <SectionHeader>
        <SectionTitle>Connected Accounts</SectionTitle>
      </SectionHeader>

      {accounts.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Hugeicon name="notification" size={48} />
          </EmptyIcon>
          <EmptyTitle>Publish events to social media</EmptyTitle>
          <EmptyText>
            Connect your Facebook Page and Instagram account to share events directly from CivicLift. After
            submitting an event, you&apos;ll get the option to post it to your social channels with one click.
          </EmptyText>
          <ConnectButton onClick={handleConnect}>
            <FacebookIcon />
            Connect Facebook &amp; Instagram
          </ConnectButton>
        </EmptyState>
      ) : (
        <AccountsList>
          {accounts.map(account => (
            <AccountRow key={account.id}>
              <AccountInfo>
                {account.platformAccountImageUrl ? (
                  <AccountAvatar src={account.platformAccountImageUrl} alt={account.platformAccountName || ''} />
                ) : (
                  <AccountAvatarPlaceholder>
                    {account.platform === 'facebook_page' ? <FacebookIcon /> : <InstagramIcon />}
                  </AccountAvatarPlaceholder>
                )}
                <AccountDetails>
                  <AccountName>{account.platformAccountName || 'Unknown Account'}</AccountName>
                  <AccountPlatform>{platformLabel(account.platform)}</AccountPlatform>
                </AccountDetails>
                <AccountStatusBadge status={account.status}>{account.status}</AccountStatusBadge>
              </AccountInfo>
              <AccountActions>
                {account.status === 'expired' && (
                  <ReconnectButton onClick={handleConnect}>Reconnect</ReconnectButton>
                )}
                <DisconnectButton onClick={() => handleDisconnect(account.id)}>Disconnect</DisconnectButton>
              </AccountActions>
            </AccountRow>
          ))}
        </AccountsList>
      )}

      <InfoBox>
        <InfoTitle>How it works</InfoTitle>
        <InfoList>
          <li>Connect your Facebook Page (Instagram connects automatically if linked)</li>
          <li>Submit an event as usual through the event form</li>
          <li>After submission, choose which accounts to share to and customize the caption</li>
          <li>Posts go out immediately or can be scheduled for later</li>
        </InfoList>
      </InfoBox>
    </Container>
  )
}

const FacebookIcon = () => (
  <Hugeicon name="facebook" size={16} />
)

const InstagramIcon = () => (
  <Hugeicon name="instagram" size={16} />
)

export default SocialAccountsManager

const Container = styled.div``

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #102638;
  margin: 0;
`

const ConnectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1877f2;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #166fe5;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 32px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
`

const EmptyIcon = styled.div`
  color: #9ca3af;
  margin-bottom: 16px;
`

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 8px;
`

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  max-width: 480px;
  margin: 0 auto 24px;
  line-height: 1.6;
`

const AccountsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const AccountAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`

const AccountAvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`

const AccountDetails = styled.div``

const AccountName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #102638;
`

const AccountPlatform = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const AccountStatusBadge = styled.span<{ status: string }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) => (status === 'active' ? '#dcfce7' : '#fef3c7')};
  color: ${({ status }) => (status === 'active' ? '#166534' : '#92400e')};
`

const AccountActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ReconnectButton = styled.button`
  font-size: 13px;
  font-weight: 500;
  color: #1877f2;
  background: none;
  border: 1px solid #1877f2;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    background: #f0f7ff;
  }
`

const DisconnectButton = styled.button`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    color: #dc2626;
    border-color: #dc2626;
  }
`

const InfoBox = styled.div`
  margin-top: 32px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 8px;
  border: 1px solid #dbeafe;
`

const InfoTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 8px;
`

const InfoList = styled.ol`
  font-size: 13px;
  color: #374151;
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
`

const LoadingText = styled.p`
  color: #6b7280;
  text-align: center;
  padding: 48px 0;
`
